import { useCallback, useMemo, useRef, useState } from 'react'

import { debounce } from 'lodash'

import { formatInputMoneyToNumber } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'

export interface BudgetSettingsData {
  budgetDaily: InputMoneyValuesOnChange | null
  budgetCpm: InputMoneyValuesOnChange | null
}

export function useStandardBudgetForm({
  isLoading,
  values,
  reproductions,
  setRequiredData,
  hasToFillFields,
  setBudgetSettingsData,
  budgetSettingsData,
  isForm
}: BudgetComponentProps) {
  const { lengthCentsCurrency } = useAppInfo()

  const [formState, setFormState] = useState<BudgetSettingsData>({
    budgetDaily: null,
    budgetCpm: null
  })

  const { budgetDaily: budgetDaily } = formState

  const handleDebounce = useRef(
    debounce((func: () => void) => func(), 500)
  ).current

  const isDailyBudgetValid = useCallback(
    (value: number) => {
      return (
        values?.minDailyBudgetValue &&
        values?.minDailyBudgetValue >= 0 &&
        value &&
        value >= values?.minDailyBudgetValue
      )
    },
    [values?.minDailyBudgetValue]
  )
  const hasNoInventory =
    !hasToFillFields && reproductions.totalReproductions === 0

  const formFields = useMemo(() => {
    if (!isForm) return null

    const dailyBudgetValue = formatInputMoneyToNumber(
      budgetDaily,
      lengthCentsCurrency
    )

    return {
      budgetDaily: {
        key: 'budgetDaily',
        name: 'budget.daily',
        value: budgetDaily,
        numberValue: dailyBudgetValue,
        isValid: !!isDailyBudgetValid(dailyBudgetValue),
        isDisabled: hasToFillFields || hasNoInventory,
        showBadgeIsInvalid: !isDailyBudgetValid(dailyBudgetValue) && !isLoading,
        showBadgeIsValid: !!isDailyBudgetValid(dailyBudgetValue) && !isLoading
      }
    }
  }, [
    budgetDaily,
    isDailyBudgetValid,
    isLoading,
    hasToFillFields,
    hasNoInventory,
    isForm,
    lengthCentsCurrency
  ])

  function handleChange(value: InputMoneyValuesOnChange, name: string) {
    if (!isForm) return
    setFormState((prev: BudgetSettingsData) => ({
      ...prev,
      [name]: value
    }))

    const fieldValue = formatInputMoneyToNumber(value, lengthCentsCurrency)

    handleDebounce(() =>
      setBudgetSettingsData((prev: BudgetSettingsData) => ({
        ...prev,
        [name]: fieldValue
      }))
    )
  }

  const sectionIsValid = useMemo(() => {
    const cpmValue = !!(values?.cpmValue || null)
    const dailyBudgetIsValid = !!(formFields?.budgetDaily?.isValid || null)
    const dailyReproductionsValue = !!(values?.dailyReproductionsValue || null)
    const isInvestmentValid =
      cpmValue && dailyBudgetIsValid && dailyReproductionsValue

    setRequiredData((prev: RequiredDataToSubmit) => ({
      ...prev,
      isInvestmentValid: isInvestmentValid
    }))

    return isInvestmentValid
  }, [
    formFields?.budgetDaily?.isValid,
    values?.cpmValue,
    values?.dailyReproductionsValue,
    setRequiredData
  ])

  return {
    handleChange,
    sectionIsValid,
    hasNoInventory,
    minDailyBudgetValue: values?.minDailyBudgetValue,
    formFields,
    budgetSettingsData
  }
}
